import { prefersReducedMotion } from '@theme/utilities';

/**
 * Plays the section's muted clips while they are on screen and pauses them once they leave,
 * so off-screen videos are neither downloaded nor decoded.
 *
 * Visitors who prefer reduced motion get the cover image with native controls instead.
 */
class UgcVideos extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const videos = this.querySelectorAll('video');

    if (prefersReducedMotion()) {
      for (const video of videos) video.controls = true;
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: 0.25 });

    for (const video of videos) {
      // Autoplay is only allowed for muted videos, and the attribute alone is not always honoured
      video.muted = true;
      this.#observer.observe(video);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);

      if (entry.isIntersecting) {
        // Rejects when the browser blocks autoplay (e.g. Low Power Mode); the cover image stays
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  };
}

if (!customElements.get('ugc-videos')) {
  customElements.define('ugc-videos', UgcVideos);
}
